import { useState, useEffect, useCallback } from 'react';
import AuthService from '../services/AuthService';
import axios from 'axios';

const authService = new AuthService();

const SUBSCRIPTION_HIERARCHY = {
  simple: 1,
  advanced: 2,
  professional: 3
};

export interface SubscriptionInfo {
  planType?: string;
  subscriptionStatus?: string;
  subscriptionEndDate?: string;
  roles?: string[];
  hasActiveSubscription: boolean;
  isSimple: boolean;
  isAdvanced: boolean;
  isProfessional: boolean;
  isAdmin: boolean;
  // Free tier fields
  imageCount?: number;
  imageLimit?: number;
  remaining?: number;
  canUploadMore?: boolean;
  isFreeUser?: boolean;
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    hasActiveSubscription: false,
    isSimple: false,
    isAdvanced: false,
    isProfessional: false,
    isAdmin: false
  });
  const [loading, setLoading] = useState(true);
  
  // Get default free tier limit from env (or fallback to 15)
  const defaultLimit = parseInt(process.env.REACT_APP_LIMIT || '15', 10);

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      const user = await authService.getUser();
      
      if (!user || !user.profile) {
        setSubscription({
          hasActiveSubscription: false,
          isSimple: false,
          isAdvanced: false,
          isProfessional: false,
          isAdmin: false,
          imageCount: 0,
          imageLimit: defaultLimit,
          remaining: defaultLimit,
          canUploadMore: true,
          isFreeUser: true
        });
        setLoading(false);
        return;
      }

      const planType = user.profile.planType as string | undefined;
      const subscriptionStatus = user.profile.subscriptionStatus as string | undefined;
      const subscriptionEndDate = user.profile.subscriptionEndDate as string | undefined;
      
      // Extract roles from profile
      const rolesClaim = user.profile.role;
      const roles = Array.isArray(rolesClaim) ? rolesClaim : (rolesClaim ? [rolesClaim] : []);
      const isAdmin = roles.includes('admin') || roles.includes('Admin');

      const isActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
      const userLevel = SUBSCRIPTION_HIERARCHY[planType as keyof typeof SUBSCRIPTION_HIERARCHY] || 0;

      // Fetch usage stats from backend
      let usageData = {
        imageCount: 0,
        imageLimit: defaultLimit,
        remaining: defaultLimit,
        canUploadMore: true,
        isFreeUser: !isActive
      };

      try {
        const token = user.access_token;
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        console.log(`[🔄] loadSubscriptionInfo: Calling ${apiUrl}/user/usage`);
        console.log('[🔑] Token:', token?.substring(0, 20) + '...');
        
        const response = await axios.get(`${apiUrl}/user/usage`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('[📦] loadSubscriptionInfo response:', response.data);
        
        if (response.data) {
          usageData = {
            imageCount: response.data.imageCount ?? 0,
            imageLimit: response.data.imageLimit ?? defaultLimit,
            remaining: response.data.remaining ?? defaultLimit,
            canUploadMore: isActive || response.data.canUploadMore, // Paid users can always upload
            isFreeUser: !isActive
          };
          console.log('[✅] loadSubscriptionInfo: usageData =', usageData);
        }
      } catch (error) {
        console.error('[✗] Error fetching usage stats:', error);
        if (axios.isAxiosError(error)) {
          console.error('[✗] Response status:', error.response?.status);
          console.error('[✗] Response data:', error.response?.data);
        }
        // Use defaults if API call fails
      }

      setSubscription({
        planType,
        subscriptionStatus,
        subscriptionEndDate,
        roles,
        hasActiveSubscription: isActive,
        isSimple: isActive && userLevel >= SUBSCRIPTION_HIERARCHY.simple,
        isAdvanced: isActive && userLevel >= SUBSCRIPTION_HIERARCHY.advanced,
        isProfessional: isActive && userLevel >= SUBSCRIPTION_HIERARCHY.professional,
        isAdmin,
        ...usageData
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading subscription:', error);
      setLoading(false);
    }
  };

  const hasSubscription = (requiredPlan: 'simple' | 'advanced' | 'professional'): boolean => {
    if (!subscription.hasActiveSubscription) return false;
    
    const userLevel = SUBSCRIPTION_HIERARCHY[subscription.planType as keyof typeof SUBSCRIPTION_HIERARCHY] || 0;
    const requiredLevel = SUBSCRIPTION_HIERARCHY[requiredPlan];
    
    return userLevel >= requiredLevel;
  };

  const refreshSubscription = async () => {
    setLoading(true);
    // First refresh the claims from IdentityServer
    const refreshed = await authService.refreshUserClaims();
    if (refreshed) {
      // Then reload the subscription info from the refreshed claims
      await loadSubscriptionInfo();
    } else {
      setLoading(false);
    }
  };

  const refreshUsageOnly = useCallback(async () => {
    // Lightweight refresh - only fetch usage count without refreshing JWT claims
    // Get default free tier limit from env (or fallback to 15)
    const defaultLimit = parseInt(process.env.REACT_APP_LIMIT || '15', 10);
    
    try {
      console.log('[🔄] Refreshing usage count only (no JWT refresh)');
      const user = await authService.getUser();
      if (!user || !user.access_token) {
        console.log('[⚠️] No user/token available for usage refresh');
        return;
      }

      const token = user.access_token;
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      console.log(`[🔄] refreshUsageOnly: Calling ${apiUrl}/user/usage`);
      console.log('[🔑] Token:', token?.substring(0, 20) + '...');
      
      const response = await axios.get(`${apiUrl}/user/usage`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('[📦] Full response:', response);
      console.log('[📦] Response data:', response.data);

      if (response.data) {
        console.log('[✅] Usage count refreshed:', response.data);
        // Update only usage fields without touching subscription status
        setSubscription(prev => ({
          ...prev,
          imageCount: response.data.imageCount ?? 0,
          imageLimit: response.data.imageLimit ?? defaultLimit,
          remaining: response.data.remaining ?? defaultLimit,
          canUploadMore: prev.hasActiveSubscription || response.data.canUploadMore,
          isFreeUser: !prev.hasActiveSubscription
        }));
      } else {
        console.log('[⚠️] No data in response');
      }
    } catch (error) {
      console.error('[✗] Error refreshing usage count:', error);
      if (axios.isAxiosError(error)) {
        console.error('[✗] Response status:', error.response?.status);
        console.error('[✗] Response data:', error.response?.data);
      }
    }
  }, []);  // Empty deps - authService is stable

  return {
    ...subscription,
    loading,
    hasSubscription,
    refresh: refreshSubscription,
    refreshUsage: refreshUsageOnly  // New: lightweight usage-only refresh
  };
}
